/**
 * A line of text as filled vector OUTLINES rather than text, for the burn-in
 * worker's text watermark (VE-30011).
 *
 * A watermark drawn with `BT ... Tj ET` is real text: it is selectable, it
 * turns up in search, and it is concatenated into whatever a downstream tool
 * extracts from the released record — "CONFIDENTIAL" spliced into the middle
 * of a witness statement. A watermark is decoration, so it is drawn as paths
 * and leaves nothing for an extractor to find. (The Bates stamp is the
 * opposite case — an identifier a reader must be able to search for — and
 * stays text.)
 *
 * mupdf.js has no glyph-outline API, but its SVG document writer emits every
 * glyph as a `<path>` in 1-em glyph space (y-up) plus a `<use>` per glyph
 * carrying that glyph's full text matrix. Showing the string with an identity
 * matrix and an identity device transform makes each `<use>` matrix the pen
 * position in em units, so the outlines come back line-origin-relative and can
 * be scaled to any font size with one `cm`. `Text.showString` uses the font's
 * own advances — the same ones a `Tj` with that font would — and falls back to
 * mupdf's bundled fonts for characters the base font lacks. Those cover CJK,
 * Cyrillic and Greek, not every script: a visible character NO font can draw
 * (Hebrew, Arabic, Thai, emoji, …) throws rather than vanishing — see
 * `assertEveryGlyphDrawable`.
 *
 * The SVG is parsed STRICTLY: an element, path command, attribute shape or
 * unparsed `<use>` this module does not know throws. A burn-in that fails
 * loudly, naming what it could not draw, can be fixed; a mark silently drawn
 * with a glyph missing goes out on a released record.
 *
 * ⚠️ Like the worker's other static imports, this module must import nothing
 * at runtime — the mupdf module is passed in.
 */

type Mupdf = typeof import('mupdf');
type MupdfFont = InstanceType<Mupdf['Font']>;
type Mat = [number, number, number, number, number, number];

/** A line's outlines as PDF path operators, ready to fill with `f`. */
export interface GlyphOutlineLine {
  /** `m`/`l`/`c`/`h` operators in em units, line origin at 0,0, y-up. */
  ops: string;
  /** Bounds of every emitted point, control points included, in em units. */
  bbox: [number, number, number, number];
}

/** Em coordinates to 5 decimals — 0.005pt at a 400pt mark, the size ceiling. */
function fmt(n: number): string {
  // `Number(...)` drops trailing zeros and turns "-0.00000" into "0".
  return String(Number(n.toFixed(5)));
}

const NUMBER = /[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/y;

/**
 * Parse one glyph's SVG path data into PDF operators under `m`.
 *
 * Only what mupdf's SVG device emits for a glyph is accepted: absolute
 * `M L H V C Z`, with implicit repetition of the preceding command's
 * coordinate groups (an implicit group after `M` is a line-to, per SVG).
 */
function pathToPdfOps(
  d: string,
  m: Mat,
  out: string[],
  bbox: [number, number, number, number]
): void {
  const pt = (x: number, y: number): string => {
    const px = m[0] * x + m[2] * y + m[4];
    const py = m[1] * x + m[3] * y + m[5];
    bbox[0] = Math.min(bbox[0], px);
    bbox[1] = Math.min(bbox[1], py);
    bbox[2] = Math.max(bbox[2], px);
    bbox[3] = Math.max(bbox[3], py);
    return `${fmt(px)} ${fmt(py)}`;
  };
  let i = 0;
  const skipSep = () => {
    while (i < d.length && /[\s,]/.test(d.charAt(i))) {
      i++;
    }
  };
  const atNumber = (): boolean => {
    skipSep();
    NUMBER.lastIndex = i;
    return NUMBER.test(d);
  };
  const num = (): number => {
    skipSep();
    NUMBER.lastIndex = i;
    const match = NUMBER.exec(d);
    if (!match) {
      throw new Error(`glyph outline: expected a number at ${i} in "${d}"`);
    }
    i = NUMBER.lastIndex;
    const value = Number(match[0]);
    // `1e999` parses to Infinity and would format as text PDF cannot read.
    if (!Number.isFinite(value)) {
      throw new Error(`glyph outline: non-finite number in "${d}"`);
    }
    return value;
  };

  // Current point and subpath start, in GLYPH space — `H`/`V` need them.
  let cx = 0;
  let cy = 0;
  let sx = 0;
  let sy = 0;
  let cmd = '';
  for (;;) {
    skipSep();
    if (i >= d.length) {
      return;
    }
    const ch = d.charAt(i);
    if (/[A-Za-z]/.test(ch)) {
      cmd = ch;
      i++;
    } else if (cmd === '' || cmd === 'Z') {
      throw new Error(`glyph outline: coordinates with no command in "${d}"`);
    }
    // Otherwise: an implicit repeat of `cmd`.
    switch (cmd) {
      case 'M':
        cx = num();
        cy = num();
        sx = cx;
        sy = cy;
        out.push(`${pt(cx, cy)} m`);
        cmd = 'L';
        break;
      case 'L':
        cx = num();
        cy = num();
        out.push(`${pt(cx, cy)} l`);
        break;
      case 'H':
        cx = num();
        out.push(`${pt(cx, cy)} l`);
        break;
      case 'V':
        cy = num();
        out.push(`${pt(cx, cy)} l`);
        break;
      case 'C': {
        const p1 = pt(num(), num());
        const p2 = pt(num(), num());
        cx = num();
        cy = num();
        out.push(`${p1} ${p2} ${pt(cx, cy)} c`);
        break;
      }
      case 'Z':
        out.push('h');
        cx = sx;
        cy = sy;
        if (atNumber()) {
          throw new Error(`glyph outline: coordinates after Z in "${d}"`);
        }
        break;
      default:
        throw new Error(
          `glyph outline: unsupported path command "${cmd}" in "${d}"`
        );
    }
  }
}

/** The SVG elements a glyph-outline render consists of — nothing else. */
const ALLOWED_ELEMENTS = new Set(['svg', 'defs', 'path', 'use']);

/**
 * An unpaired surrogate is dropped before it reaches the WASM boundary. It
 * encodes no character, so there is nothing to draw — and as U+FFFD it would
 * have no glyph either, failing the burn-in over a half of a code unit pair.
 *
 * A loop, not a regex: the regex form needs a lookbehind, which older Safari
 * cannot parse — and a parse error in a worker module takes down every burn-in,
 * not just this mark.
 */
function withoutLoneSurrogates(s: string): string {
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff) {
      const next = s.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        out += s.charAt(i) + s.charAt(i + 1);
        i++;
        continue;
      }
    } else if (c < 0xdc00 || c > 0xdfff) {
      out += s.charAt(i);
    }
  }
  return out;
}

/** Characters that draw nothing anyway, so having no glyph loses nothing. */
const INVISIBLE = /^[\s\p{Cf}]$/u;

/**
 * Refuse a line any of whose visible characters has no glyph (VE-30011).
 *
 * `showString` maps a character that neither the base font nor mupdf's
 * fallback fonts can draw — Hebrew, Arabic, Thai, Devanagari, emoji, U+FFFD —
 * to glyph 0, `.notdef`, whose outline for these fonts is EMPTY. Left alone the
 * character simply vanishes, and a line made only of such characters vanishes
 * whole, while the export reports success: a released record missing a
 * marking nobody can see is missing. The old text path drew mojibake instead,
 * which is wrong but visible; failing the burn-in is the loud version of that.
 *
 * A NUL is refused up front: it ends the string at the WASM boundary, so
 * everything after it would vanish without even reaching glyph lookup.
 */
function assertEveryGlyphDrawable(text: InstanceType<Mupdf['Text']>): void {
  // RECORD in the callback, throw after `walk` returns. `walk` is C calling
  // back into JS; an exception thrown inside the callback unwinds straight
  // through the C frame, skipping mupdf's try-frame pop and the stack restore.
  // The burn-in worker is reused across exports, so every such failure left it
  // a little more corrupt — a few hundred of them and later mupdf calls trap
  // (`memory access out of bounds`) inside the process that applies
  // redactions. Nothing in here may throw, `String.fromCodePoint` included,
  // hence the range check before it.
  let undrawable: number | null = null;
  text.walk({
    showGlyph(_font, _trm, glyph, unicode) {
      if (undrawable !== null || glyph !== 0) {
        return;
      }
      const isCodePoint =
        Number.isInteger(unicode) && unicode >= 0 && unicode <= 0x10ffff;
      if (!isCodePoint || !INVISIBLE.test(String.fromCodePoint(unicode))) {
        undrawable = unicode;
      }
    },
  });
  if (undrawable !== null) {
    throw new Error(undrawableWatermarkMessage(undrawable));
  }
}

/** `U+05E1` — how the error, and the toast built from it, name a character. */
function codePointLabel(codePoint: number): string {
  return `U+${codePoint.toString(16).toUpperCase().padStart(4, '0')}`;
}

/**
 * The error a watermark character with no glyph raises. One function writes
 * it and `parseUndrawableWatermarkError` reads it back, so the export saga can
 * tell the user what is wrong instead of "please try again" — the failure is
 * the same on every retry (VE-30011).
 */
function undrawableWatermarkMessage(codePoint: number): string {
  return `watermark character ${codePointLabel(codePoint)} has no glyph in any available font`;
}

const UNDRAWABLE_WATERMARK =
  /watermark character (U\+[0-9A-F]{4,6}) has no glyph/;

/**
 * The offending character's label (`U+05E1`) when a burn-in failed because a
 * watermark contains a character no font can draw; otherwise null. Reads the
 * worker's message through any `context: ` prefixes the worker adds.
 */
export function parseUndrawableWatermarkError(message: string): string | null {
  return UNDRAWABLE_WATERMARK.exec(message)?.[1] ?? null;
}

/**
 * `line` in `font` as filled outlines, in em units relative to the line's
 * origin on the baseline. `null` when there is nothing to draw (an empty or
 * all-space line).
 */
export function glyphOutlineLine(
  mupdf: Mupdf,
  font: MupdfFont,
  line: string
): GlyphOutlineLine | null {
  if (!line) {
    return null;
  }
  const identity: Mat = [1, 0, 0, 1, 0, 0];
  const text = new mupdf.Text();
  const buf = new mupdf.Buffer();
  let svg: string;
  if (line.includes('\u0000')) {
    throw new Error(undrawableWatermarkMessage(0));
  }
  try {
    text.showString(font, identity, withoutLoneSurrogates(line));
    assertEveryGlyphDrawable(text);
    const writer = new mupdf.DocumentWriter(buf, 'svg', 'text=path');
    try {
      // `beginPage` hands back a KEPT reference, so this device is one more
      // thing to destroy and not the writer's to free. Left to the
      // FinalizationRegistry it is freed whenever JS happens to collect — and
      // JS cannot see the WASM heap the burn-in worker is reusing across
      // exports. Measured: ~9KB per line, 64MB over 4000 lines.
      const dev = writer.beginPage([0, 0, 1, 1]);
      try {
        dev.fillText(text, identity, mupdf.ColorSpace.DeviceRGB, [0, 0, 0], 1);
      } finally {
        dev.destroy();
      }
      writer.endPage();
      writer.close();
    } finally {
      writer.destroy();
    }
    svg = buf.asString();
  } finally {
    text.destroy();
    buf.destroy();
  }

  for (const tag of svg.matchAll(/<\/?([A-Za-z][\w:-]*)/g)) {
    if (!ALLOWED_ELEMENTS.has(tag[1] ?? '')) {
      throw new Error(`glyph outline: unexpected <${tag[1]}> in SVG render`);
    }
  }

  // Glyph definitions, by id. Attribute values are matched quote-aware so a
  // `>` inside one (mupdf writes the source character into `data-text`) does
  // not end the element early.
  const defs = new Map<string, string>();
  for (const p of svg.matchAll(/<path\b((?:[^>"]|"[^"]*")*)\/>/g)) {
    const attrs = p[1] ?? '';
    const id = /\bid="([^"]+)"/.exec(attrs)?.[1];
    const d = /\bd="([^"]*)"/.exec(attrs)?.[1];
    if (id === undefined || d === undefined) {
      // The element is not echoed: it can carry mupdf's `data-text`, i.e. the
      // watermark's own characters, into logs.
      throw new Error('glyph outline: <path> without id/d');
    }
    defs.set(id, d);
  }

  const out: string[] = [];
  const bbox: [number, number, number, number] = [
    Infinity,
    Infinity,
    -Infinity,
    -Infinity,
  ];
  // Every `<use>` must be one this parser read. A non-self-closing form from a
  // future mupdf would otherwise match nothing and drop the line silently.
  const useOpenings = svg.match(/<use\b/g)?.length ?? 0;
  let usesParsed = 0;
  for (const u of svg.matchAll(/<use\b((?:[^>"]|"[^"]*")*)\/>/g)) {
    usesParsed++;
    const attrs = u[1] ?? '';
    const href = /\bxlink:href="#([^"]+)"/.exec(attrs)?.[1];
    const d = href === undefined ? undefined : defs.get(href);
    if (d === undefined) {
      throw new Error(`glyph outline: <use> #${usesParsed} with no glyph`);
    }
    let m = identity;
    const transform = /\btransform="([^"]*)"/.exec(attrs)?.[1];
    if (transform !== undefined) {
      const mm = /^matrix\(([^)]*)\)$/.exec(transform.trim());
      const nums = (mm?.[1] ?? '').split(/[\s,]+/).map(Number);
      if (!mm || nums.length !== 6 || nums.some((n) => !Number.isFinite(n))) {
        throw new Error(`glyph outline: unsupported transform "${transform}"`);
      }
      m = nums as Mat;
    }
    pathToPdfOps(d, m, out, bbox);
  }
  if (usesParsed !== useOpenings) {
    throw new Error(
      `glyph outline: parsed ${usesParsed} of ${useOpenings} <use> elements`
    );
  }

  return out.length > 0 ? { ops: out.join('\n'), bbox } : null;
}
